import { useState } from "react";
import { CalendarCheck, ExternalLink } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Field, FieldError, HelpTip, labelClass, NativeSelect } from "@/components/admin/cards";
import {
  BOOKING_ENGINE_OPTIONS,
  BOOKING_MODE_OPTIONS,
  BookingSettings,
  DATE_FORMATS,
  DEFAULT_DATE_FORMAT,
  DetectedTemplate,
  PLACEHOLDERS,
  detectTemplate,
  testBookingUrl,
} from "./bookingLink";

interface BookingModeSettingsProps {
  settings: BookingSettings;
  errors: Record<string, string>;
  onChange: (patch: BookingSettings) => void;
  propertyIdRef?: React.Ref<HTMLInputElement>;
  templateRef?: React.Ref<HTMLInputElement>;
  /** Extra fields shown under the booking mode. */
  children?: React.ReactNode;
}

const MODE_DETAILS: Record<string, { description: string; icon: React.ReactNode }> = {
  staah: { description: "Guests pick rooms and pay inside the chat. Day-use packages work only here.", icon: <CalendarCheck aria-hidden="true" /> },
  link_out: { description: "Guests search in the chat, then finish booking on your booking engine.", icon: <ExternalLink aria-hidden="true" /> },
};

const BookingModeSettings = ({ settings, errors, onChange, propertyIdRef, templateRef, children }: BookingModeSettingsProps) => {
  const [sample, setSample] = useState({ url: "", checkin: "", checkout: "", adults: "2", children: "0" });
  const [detected, setDetected] = useState<DetectedTemplate | null>(null);
  const [sampleError, setSampleError] = useState("");

  const mode = settings.booking_mode || "staah";
  const engine = settings.booking_engine || "swiftbook";
  const testUrl = mode === "link_out" ? testBookingUrl({ ...settings, booking_engine: engine }) : null;

  const detect = () => {
    const result = detectTemplate(sample.url, sample.checkin, sample.checkout, sample.adults, sample.children);
    setDetected(result);
    setSampleError(
      result
        ? ""
        : "Couldn't find the check-in and check-out dates in that URL. Check the dates match the search you made.",
    );
  };

  const acceptDetected = () => {
    if (!detected) return;
    onChange({ booking_url_template: detected.template, booking_date_format: detected.dateFormat });
    setDetected(null);
  };

  const chooseMode = (value: string) =>
    onChange({
      booking_mode: value,
      ...(value === "link_out" && !settings.booking_engine ? { booking_engine: "swiftbook" } : {}),
    });

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-1">
          <span id="bookingModeLabel" className={labelClass}>How guests book rooms</span>
          <HelpTip label="How guests book rooms">Day-use packages are offered only with in-chat STAAH booking.</HelpTip>
        </div>
        <div role="radiogroup" aria-label="Booking mode" className="grid gap-3 sm:grid-cols-2">
          {BOOKING_MODE_OPTIONS.map((option) => {
            const selected = mode === option.value;
            return (
              <label
                key={option.value}
                className={cn(
                  "flex cursor-pointer items-start gap-3 rounded-lg border bg-card p-3 transition-colors has-[:focus-visible]:ring-[3px] has-[:focus-visible]:ring-ring/50",
                  selected ? "border-primary bg-primary/5" : "border-border hover:bg-accent",
                )}
              >
                <input
                  type="radio"
                  name="bookingMode"
                  className="sr-only"
                  value={option.value}
                  aria-label={option.label}
                  checked={selected}
                  onChange={() => chooseMode(option.value)}
                />
                <span className={cn("flex size-8 shrink-0 items-center justify-center rounded-md [&_svg]:size-4", selected ? "bg-primary text-primary-foreground" : "bg-secondary text-secondary-foreground")}>
                  {MODE_DETAILS[option.value]?.icon}
                </span>
                <span className="flex min-w-0 flex-col">
                  <span className="text-sm font-semibold">{option.label}</span>
                  <span className="text-xs text-muted-foreground">{MODE_DETAILS[option.value]?.description}</span>
                </span>
              </label>
            );
          })}
        </div>
      </div>

      {mode === "link_out" && (
        <Field label="Booking engine" htmlFor="bookingEngineSelect" className="sm:max-w-sm">
          <NativeSelect id="bookingEngineSelect" value={engine} onChange={(e) => onChange({ booking_engine: e.target.value })}>
            {BOOKING_ENGINE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </NativeSelect>
        </Field>
      )}

      {children}

      {mode === "link_out" && engine === "swiftbook" && (
        <Field label="Property ID" htmlFor="bookingEnginePropertyId" required error={errors.booking_engine_property_id} className="sm:max-w-sm">
          <Input
            id="bookingEnginePropertyId"
            placeholder="e.g. 4258"
            ref={propertyIdRef}
            value={settings.booking_engine_property_id || ""}
            onChange={(e) => onChange({ booking_engine_property_id: e.target.value })}
          />
        </Field>
      )}

      {mode === "link_out" && engine === "custom" && (
        <>
          <div className="grid gap-4 md:grid-cols-[2fr_1fr]">
            <Field
              label="Booking URL template"
              htmlFor="bookingUrlTemplate"
              required
              error={errors.booking_url_template}
              hint={<>Placeholders: {PLACEHOLDERS.map((p) => `{${p}}`).join(", ")}. Parameters without a value (e.g. a missing promo code) are left out of the link.</>}
            >
              <Input
                id="bookingUrlTemplate"
                placeholder="https://your-booking-engine.com/search?checkin={checkin}&checkout={checkout}"
                ref={templateRef}
                value={settings.booking_url_template || ""}
                onChange={(e) => onChange({ booking_url_template: e.target.value })}
              />
            </Field>
            <Field label="Date format" htmlFor="bookingDateFormat">
              <NativeSelect
                id="bookingDateFormat"
                value={settings.booking_date_format || DEFAULT_DATE_FORMAT}
                onChange={(e) => onChange({ booking_date_format: e.target.value })}
              >
                {DATE_FORMATS.map((format) => <option key={format} value={format}>{format}</option>)}
              </NativeSelect>
            </Field>
          </div>

          <div className="flex flex-col gap-3 rounded-lg border border-border bg-card p-4">
            <div className="flex items-center gap-1">
              <p className={labelClass}>Paste a sample URL</p>
              <HelpTip label="Paste a sample URL">
                Search on your booking engine, copy the results page URL and enter the dates, adults and children you searched
                with. We&apos;ll turn it into a template.
              </HelpTip>
            </div>
            <Input
              aria-label="Sample booking URL"
              placeholder="https://your-booking-engine.com/search?arrive=2026-12-16&depart=2026-12-17&adults=2"
              value={sample.url}
              onChange={(e) => setSample((prev) => ({ ...prev, url: e.target.value }))}
            />
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <Field label="Check-in searched" htmlFor="sampleCheckin">
                <Input id="sampleCheckin" type="date" value={sample.checkin}
                  onChange={(e) => setSample((prev) => ({ ...prev, checkin: e.target.value }))} />
              </Field>
              <Field label="Check-out searched" htmlFor="sampleCheckout">
                <Input id="sampleCheckout" type="date" value={sample.checkout}
                  onChange={(e) => setSample((prev) => ({ ...prev, checkout: e.target.value }))} />
              </Field>
              <Field label="Adults searched" htmlFor="sampleAdults">
                <Input id="sampleAdults" type="number" min="1" value={sample.adults}
                  onChange={(e) => setSample((prev) => ({ ...prev, adults: e.target.value }))} />
              </Field>
              <Field label="Children searched" htmlFor="sampleChildren">
                <Input id="sampleChildren" type="number" min="0" value={sample.children}
                  onChange={(e) => setSample((prev) => ({ ...prev, children: e.target.value }))} />
              </Field>
            </div>
            <div>
              <Button type="button" variant="outline" size="sm" onClick={detect}
                disabled={!sample.url.trim() || !sample.checkin || !sample.checkout}>
                Detect template
              </Button>
            </div>
            <FieldError message={sampleError} />
            {detected && (
              <div className="flex flex-col gap-2">
                <p className="text-sm">
                  Date format: <strong>{detected.dateFormat}</strong>
                  {!detected.adultsDetected && " (adults not found in the URL)"}
                  {!detected.childrenDetected && " (children not found in the URL)"}
                </p>
                <code className="block break-all rounded-md bg-muted px-2 py-1 text-xs">{detected.template}</code>
                <div className="flex gap-2">
                  <Button type="button" size="sm" onClick={acceptDetected}>Use this template</Button>
                  <Button type="button" size="sm" variant="outline" onClick={() => setDetected(null)}>Discard</Button>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {mode === "link_out" && (
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={!testUrl}
            onClick={() => testUrl && window.open(testUrl, "_blank", "noopener,noreferrer")}
          >
            <ExternalLink aria-hidden="true" />
            Test link
          </Button>
          <HelpTip label="Test link">Opens your booking engine for a 1-night stay 30 days from today.</HelpTip>
        </div>
      )}
    </div>
  );
};

export default BookingModeSettings;
